import type { CrossCalendarDef } from '@/lib/cross-calendar-pages';
import { SectionShell } from '@/components/layout/SectionShell';
import { getCrossCalendarDefsForDest } from '@/lib/cross-calendar-pages';
import { getDestinationMonth, getVisaRequirement, getTranslatedEventTitle } from '@/lib/data-source';
import { getOriginVacationWindows } from '@/lib/cross-calendar';
import { JsonLdScript } from '@/components/lib/JsonLdScript';
import { buildHowToJsonLd } from '@/lib/seo';
import { FaqBlock } from './FaqBlock';
import { BestMatchCallout } from './BestMatchCallout';
import { MonthBreakdownList } from './MonthBreakdownList';
import { CALENDAR_YEAR } from '@/lib/config';
import { getMonthName } from '@/lib/constants';
import { SITE_URL } from '@/lib/brand';
import { ui, uiFb, getTemplateStr, getCountryNameEntry } from '@/lib/i18n';
import type { EvergreenSlugEntry } from '@/lib/i18n';
import { VISA_PHRASE_KEYS, VISA_COLORS } from '@/lib/visa-constants';
import { CrossCalendarOriginSelector } from './CrossCalendarOriginSelector';
import type { CountryCode } from '@/lib/types';

const YEAR = CALENDAR_YEAR;

interface CrossCalendarSeoSectionProps {
  def: CrossCalendarDef;
  entry: EvergreenSlugEntry;
}

export function CrossCalendarSeoSection({ def, entry }: CrossCalendarSeoSectionProps) {
  const lang = entry.lang ?? 'en';

  // Translate country names for the current language
  const originDisplay = getCountryNameEntry(def.originCode as CountryCode, lang).display;
  const destDisplay = getCountryNameEntry(def.dest.countryCode, lang).display;

  // Sibling origins for the same destination
  const siblingDefs = getCrossCalendarDefsForDest(def.dest.slugPrefix);
  const siblings = siblingDefs.map(d => ({
    slug: d.slug,
    originCode: d.originCode,
    originFlag: d.originFlag,
    originName: d.originName,
  }));

  // Visa requirement for this origin→destination pair
  const visa = getVisaRequirement(def.originCode, def.dest.countryCode);

  // Build month-by-month data combining origin holidays + destination conditions
  const monthData = def.overlapMonths.map(m => {
    const vw = getOriginVacationWindows(def.originCode, m);
    const dm = getDestinationMonth(def.dest.countryCode, m);
    const holidayNames = [
      ...vw.holidays.map(h => getTranslatedEventTitle(h.id, lang, h.title)),
      ...vw.originHolidays.map(h => h.name),
    ];
    return { month: m, vw, dm, holidayNames };
  });

  // Find the best month: highest combined score
  const scored = monthData.map(md => ({
    ...md,
    score: (md.dm?.weatherScore ?? 3) + md.vw.totalDaysOff,
  }));
  const bestMonth = scored.reduce((best, cur) => cur.score > best.score ? cur : best, scored[0]);

  // FAQs
  const faqs = [
    {
      question: getTemplateStr('seo.cross.faq1_q', lang, { destination: destDisplay, origin: originDisplay }),
      answer: bestMonth
        ? getTemplateStr('seo.cross.faq1_a_best', lang, {
            month: getMonthName(bestMonth.month - 1, lang),
            holidays: bestMonth.holidayNames.join(', '),
            destination: destDisplay,
            crowds: bestMonth.dm?.crowdLevel ?? 'moderate',
            weather: String(bestMonth.dm?.weatherScore ?? 3),
          })
        : getTemplateStr('seo.cross.faq1_a_fallback', lang, {}),
    },
    {
      question: getTemplateStr('seo.cross.faq2_q', lang, { origin: originDisplay, destination: destDisplay }),
      answer: (() => {
        const peakOverlaps = monthData.filter(md => md.dm && md.dm.weatherScore >= 4);
        if (peakOverlaps.length === 0) return getTemplateStr('seo.cross.faq2_a_fallback', lang, {});
        const names = peakOverlaps.flatMap(md => md.holidayNames).slice(0, 5);
        return getTemplateStr('seo.cross.faq2_a_match', lang, { holidays: names.join(', '), destination: destDisplay });
      })(),
    },
    {
      question: getTemplateStr('seo.cross.faq3_q', lang, { destination: destDisplay, origin: originDisplay }),
      answer: (() => {
        const highCrowdMonths = monthData.filter(md => md.dm?.crowdLevel === 'high');
        if (highCrowdMonths.length === 0) return getTemplateStr('seo.cross.faq3_a_low', lang, { origin: originDisplay, destination: destDisplay });
        return getTemplateStr('seo.cross.faq3_a_high', lang, { months: highCrowdMonths.map(md => getMonthName(md.month - 1, lang)).join(', ') });
      })(),
    },
    ...(visa ? [{
      question: getTemplateStr('seo.cross.faq4_q', lang, { destination: destDisplay, origin: originDisplay }),
      answer: (() => {
        const visaLabel = ui(VISA_PHRASE_KEYS[visa.visaCategory], lang);
        if (visa.visaCategory === 'visa_free') {
          return visa.durationDays
            ? getTemplateStr('seo.cross.faq4_a_free', lang, { origin: originDisplay, destination: destDisplay, days: String(visa.durationDays) })
            : getTemplateStr('seo.cross.faq4_a_free_no_limit', lang, { origin: originDisplay, destination: destDisplay });
        }
        if (visa.visaCategory === 'visa_on_arrival') {
          return getTemplateStr('seo.cross.faq4_a_arrival', lang, { origin: originDisplay, destination: destDisplay });
        }
        if (visa.visaCategory === 'e_visa') {
          return getTemplateStr('seo.cross.faq4_a_evisa', lang, { origin: originDisplay, destination: destDisplay });
        }
        if (visa.visaCategory === 'not_allowed') {
          return getTemplateStr('seo.cross.faq4_a_not_allowed', lang, { destination: destDisplay, origin: originDisplay });
        }
        return getTemplateStr('seo.cross.faq4_a_required', lang, { origin: originDisplay, label: visaLabel.toLowerCase(), destination: destDisplay });
      })(),
    }] : []),
  ];

  // JSON-LD: TravelAction + FAQPage
  const travelActionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TravelAction',
    fromLocation: { '@type': 'Country', name: def.originName },
    toLocation: { '@type': 'TouristDestination', name: def.dest.displayName },
    description: getTemplateStr('seo.cross.jsonld_desc', lang, { origin: originDisplay, destination: destDisplay, year: String(YEAR) }),
    url: `${SITE_URL}/${def.slug}`,
    ...(visa && {
      potentialAction: {
        '@type': 'Action',
        name: ui(VISA_PHRASE_KEYS[visa.visaCategory], lang),
        ...(visa.durationDays && { description: getTemplateStr('seo.cross.visa_duration', lang, { days: String(visa.durationDays) }) }),
      },
    }),
  };
  // HowTo JSON-LD — cross-calendar planning steps
  const visaLabel = visa ? ui(VISA_PHRASE_KEYS[visa.visaCategory], lang) : '';
  const crossHowToSteps = [
    { name: `Check ${originDisplay} holidays that create long weekends`, text: `Review public holidays in ${originDisplay} that fall near weekends for ${YEAR} travel planning.` },
    { name: `Compare with ${destDisplay} weather, crowds, and prices by month`, text: `${destDisplay} conditions vary by month. Use the month-by-month breakdown below to find the best overlap.` },
    ...(bestMonth ? [{ name: `Pick the best month (we recommend ${getMonthName(bestMonth.month - 1, lang)}, score: ${bestMonth.score}/10)`, text: `${getMonthName(bestMonth.month - 1, lang)} offers the best combination of ${originDisplay} holidays and ${destDisplay} travel conditions.` }] : []),
    ...(visa ? [{ name: `Check visa requirements (${visaLabel} for ${originDisplay} citizens)`, text: `${originDisplay} citizens have ${visaLabel.toLowerCase()} access to ${destDisplay}${visa.durationDays ? ` for up to ${visa.durationDays} days` : ''}.` }] : []),
    { name: 'Export travel dates to your calendar', text: `Save your planned ${destDisplay} travel dates to Google Calendar or Outlook.` },
  ];
  const crossHowToJsonLd = buildHowToJsonLd(`How to Plan a Trip to ${destDisplay} from ${originDisplay} ${YEAR}`, crossHowToSteps);

  return (
    <SectionShell as="section" className="pb-8 pt-12">
      {/* JSON-LD */}
      <JsonLdScript data={travelActionJsonLd} />
      <JsonLdScript data={crossHowToJsonLd} />

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-text-primary md:text-3xl">
          {entry.h2Title}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-text-secondary md:text-base">
          <span>{getTemplateStr('seo.cross.subtitle_plan', lang, { year: String(YEAR) })}</span>
          <CrossCalendarOriginSelector
            currentOriginCode={def.originCode}
            currentOriginFlag={def.originFlag}
            currentOriginName={originDisplay}
            siblings={siblings}
          />
          <span>{getTemplateStr('seo.cross.subtitle_see', lang, { destination: destDisplay })}</span>
        </div>
        {visa && (
          <div className="mt-3">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${VISA_COLORS[visa.visaCategory]}`}>
              {ui(VISA_PHRASE_KEYS[visa.visaCategory], lang)}
              {visa.durationDays && ` ${getTemplateStr('seo.cross.visa_duration', lang, { days: String(visa.durationDays) })}`}
            </span>
          </div>
        )}
      </div>

      {/* Best Match callout */}
      {bestMonth && bestMonth.dm && (
        <BestMatchCallout
          month={bestMonth.month}
          holidayNames={bestMonth.holidayNames}
          dm={bestMonth.dm}
          destinationName={destDisplay}
          lang={lang}
        />
      )}

      {/* Month-by-month breakdown */}
      <MonthBreakdownList
        monthData={monthData}
        originName={originDisplay}
        originFlag={def.originFlag}
        destinationName={destDisplay}
        lang={lang}
      />

      {/* FAQ */}
      <FaqBlock faqs={faqs} heading={uiFb('ui.faq_heading', 'Frequently Asked Questions', lang)} className="border-t border-border-subtle pt-6" />
    </SectionShell>
  );
}
